class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

        // Listen for cart updates to reapply focus trap
    document.addEventListener('update-cart-render', () => {
      if (this.classList.contains('active')) {
        setTimeout(() => {
          this.reapplyFocusTrap();
        }, 250);
      }
    });
    
    // Prevent focus from escaping during DOM updates
    document.addEventListener('focusout', (event) => {
      if (this.classList.contains('active') && !this.contains(event.relatedTarget)) {
        // Focus is leaving the drawer, prevent it
        setTimeout(() => {
          if (this.classList.contains('active') && !this.contains(document.activeElement)) {
            const fallbackElement = this.querySelector('[data-link-cart-empty]') || this.querySelector('.drawer__close');
            if (fallbackElement) {
              fallbackElement.focus();
            }
          }
        }, 10);
      }
    });
  }

  connectedCallback() {
    this.removeAttribute('hidden')
  }

  // focus trapping is handled by global trapFocus utility on open()

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return; // some store aren't e-commerce
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      // Check if cart is empty by looking at the cart-drawer element class or cart state
      const isEmptyByClass = this.classList.contains('is-empty');
      const isEmptyByElements = !this.querySelector('.cart-item') || this.querySelectorAll('.cart-item').length === 0;
      const isEmpty = isEmptyByClass || isEmptyByElements;
      const containerToTrapFocusOn = isEmpty
        ? this.querySelector('.drawer__inner') || this
        : document.getElementById('CartDrawer');
      const focusElement = isEmpty 
        ? this.querySelector('[data-link-cart-empty]') || this.querySelector('.drawer__close')
        : this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    this.removeInert();

    document.body.classList.add('u-overflow-hidden');
  }

  close() {
    const body = this.querySelector('body');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('u-overflow-hidden');
    this.addInert();
  }

  removeInert() {
    this.querySelectorAll('aside.cart-drawer-side[inert]').forEach(side => {
      side.inert = false;
    });
  }

  addInert() {
    this.querySelectorAll('aside.cart-drawer-side[inert]').forEach(side => {
      side.inert = true;
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // If opened via Add to Cart, remember the current focused element to restore on close
      if (!this.activeElement) this.setActiveElement(document.activeElement);
      this.open();
      // Reapply focus trap after content is rendered
      setTimeout(() => {
        this.reapplyFocusTrap();
      }, 100);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      },
      {
        id: 'announcement-bar',
        section: document.getElementById('announcement-bar')?.dataset.id,
        selector: '.js-contents-announcement',
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
    // Force focus trap to stay active during cart transitions
  forceFocusTrapDuringTransition() {
    if (this.classList.contains('active')) {
      const currentFocusedElement = document.activeElement;
      
      // Prevent focus from leaving the drawer during DOM updates
      const observer = new MutationObserver(() => {
        if (this.classList.contains('active') && !this.contains(document.activeElement)) {
          // Focus has left the drawer, bring it back
          const fallbackElement = this.querySelector('[data-link-cart-empty]') || 
                                 this.querySelector('.drawer__close');
          if (fallbackElement) {
            fallbackElement.focus();
          }
        }
      });
      
      observer.observe(this, { childList: true, subtree: true });
      
      // Stop observing after 1 second
      setTimeout(() => {
        observer.disconnect();
        this.reapplyFocusTrap();
      }, 1000);
    }
  }

  // Method to reapply focus trap after cart updates
  reapplyFocusTrap() {
    if (this.classList.contains('active')) {
      // Check if cart is empty by looking at the cart-drawer element class or cart state
      const isEmptyByClass = this.classList.contains('is-empty');
      const isEmptyByElements = !this.querySelector('.cart-item') || this.querySelectorAll('.cart-item').length === 0;
      const isEmpty = isEmptyByClass || isEmptyByElements;
      
      // Add a delay and verify elements exist before applying trap
      setTimeout(() => {
        // Re-check elements exist after DOM update
        let containerToTrapFocusOn;
        let focusElement;
        
        if (isEmpty) {
          // When cart is empty, focus should be trapped in the entire drawer
          containerToTrapFocusOn = this.querySelector('.drawer__inner') || this;
          // Try to focus on the "start buying" link, fallback to close button
          focusElement = this.querySelector('[data-link-cart-empty]') || this.querySelector('.drawer__close');
          
          // Debug: verify empty state elements exist
          if (!focusElement) {
            console.warn('Cart drawer: No focusable element found for empty state');
            focusElement = this.querySelector('.drawer__close');
          }
        } else {
          // When cart has items, trap focus in the main cart drawer
          containerToTrapFocusOn = document.getElementById('CartDrawer') || this;
          // Focus on the first cart item or close button
          focusElement = this.querySelector('.cart-item__name') || 
                        this.querySelector('.cart-item') || 
                        this.querySelector('.drawer__close');
        }
        
        // Remove existing trap and immediately reapply with new elements
        if (containerToTrapFocusOn && focusElement) {
          removeTrapFocus();
          trapFocus(containerToTrapFocusOn, focusElement);
        } else {
          console.warn('Cart drawer: Failed to find elements for focus trap', {
            container: !!containerToTrapFocusOn,
            focusElement: !!focusElement,
            isEmpty
          });
        }
      }, 100);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer-container'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'announcement-bar',
        section: document.getElementById('announcement-bar')?.dataset.id,
        selector: '.js-contents-announcement',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
